import { request, ResponseData } from "./instance";
import { GenerateTaskInfo, GenerateTask } from "./api";

/**
 * 启动/进入做课任务
 *
 * 场景 1：新创建任务
 *   - 不传 generateId，返回新的任务信息（包含新的 generateId）
 *
 * 场景 2：进入已有任务
 *   - 传入 generateId，返回该任务的信息（白板地址、聊天历史、教案等）
 *
 * @param params.generateId - 任务 ID（可选，不传则创建新任务）
 * @returns GenerateTaskInfo
 */
export const startGenerateLesson = async (
  params?: { generateId?: string }
): Promise<ResponseData<GenerateTaskInfo>> => {
  const query = params?.generateId ? `?generateId=${params.generateId}` : "";
  const resp = await request(`generate/lesson/start${query}`);
  return resp;
};

/**
 * 重命名做课任务
 */
export const renameGenerateLesson = async (params: {
  generateId: number;
  newName: string;
}): Promise<ResponseData<GenerateTaskInfo>> => {
  const resp = await request("generate/lesson/rename", {
    method: "POST",
    data: params,
    headers: { "Content-Type": "application/json" },
  });
  return resp;
};

/**
 * 删除做课任务
 * 返回完整的排序后的任务数组
 */
export const deleteGenerateLesson = async (params: {
  generateId: number;
}): Promise<ResponseData<GenerateTask[]>> => {
  const resp = await request("generate/lesson/delete", {
    method: "POST",
    data: params,
    headers: { "Content-Type": "application/json" },
  });
  return resp;
};

/**
 * 置顶/取消置顶做课任务
 * 返回完整的排序后的任务数组
 */
export const pinGenerateLesson = async (params: {
  generateId: number;
  isPinned: number; // 1=置顶, 0=取消置顶
}): Promise<ResponseData<GenerateTask[]>> => {
  const resp = await request("generate/lesson/pin", {
    method: "POST",
    data: params,
    headers: { "Content-Type": "application/json" },
  });
  return resp;
};

/**
 * 获取课件预览链接
 *
 * @param params.generateId - 任务生成 ID
 * @returns 预览 URL、lessonId 和课程类型
 */
export const getLessonPreview = async (params: {
  generateId: number;
}): Promise<ResponseData<{
  previewUrl: string;
  lessonId: number;
  success: boolean;
  type: string;
  courseType: string;
}>> => {
  const resp = await request("generate/lesson/preview", {
    method: "POST",
    data: params,
    headers: { "Content-Type": "application/json" },
  });
  return resp;
};

/**
 * 发布课件
 *
 * @param params.generateId - 任务生成 ID
 * @returns 发布结果，包含课程ID、单元ID、课时ID等信息
 */
export const publishLesson = async (params: {
  generateId: number;
}): Promise<ResponseData<{
  success: boolean;
  courseId?: number;
  unitId?: number;
  lessonId?: number;
  message: string;
}>> => {
  const resp = await request("generate/lesson/publish", {
    method: "POST",
    data: params,
    headers: { "Content-Type": "application/json" },
  });
  return resp;
};
